import loadScript from 'load-script';

import { GOOGLE_RECAPTCHA_SITEKEY } from 'common/configuration/constants';
import eventEmitter, { EventsTypes } from 'common/services/events/eventEmitter';

const CAPTCHA_URL =
  'https://www.google.com/recaptcha/api.js?onload=onloadReCaptchaCallback&render=explicit';

let loaded = false;
let promise: Promise<string> | null;
let rejector: (reason?: string) => void;
let resolver: (value: string | PromiseLike<string>) => void;

// need it because once reCaptcha script loaded,
// methods are not ready yet before the event "onload".
window.onloadReCaptchaCallback = () => {
  eventEmitter.emit(EventsTypes.RECAPTCHA_LOADED);
};

const reset = () => {
  window.grecaptcha.reset();
  promise = null;
};

// invisible reCaptcha callback
const onSubmit = (token: string) => {
  if (typeof resolver === 'function') {
    resolver(token);
  }
  reset();
};

// invisible reCaptcha callback error
const onError = () => {
  if (typeof rejector === 'function') {
    rejector('error');
  }
  reset();
};

export const injectReCaptcha = (el: string | HTMLElement) => {
  if (!window.grecaptcha) return;
  return window.grecaptcha.render(el, {
    'error-callback': onError,
    'expired-callback': onError,
    callback: onSubmit,
    sitekey: GOOGLE_RECAPTCHA_SITEKEY,
    size: 'invisible'
  });
};

// get the recaptcha's token when it's valid
// https://developers.google.com/recaptcha/docs/verify
export const getReCaptchaResponse = async (widgetId?: number) => {
  if (!promise) {
    promise = new Promise<string>((resolve, reject) => {
      // You cannot validate an account without a captcha token.
      if (!window.grecaptcha) {
        return reject('error');
      }

      resolver = resolve;
      rejector = reject;

      // Invoking the reCAPTCHA verification programmatically
      // https://developers.google.com/recaptcha/docs/invisible#programmatic_execute
      return window.grecaptcha.execute(widgetId);
    });
  }
  return promise;
};

/**
 * This module / function allows the dynamic loading of ReCaptcha widget
 * javascript library
 * @param  {Function} callback - the function to execute once ReCaptcha widget api is loaded
 * @param  {Object}   options  - options for the loading of the script
 * @return {void}
 */
export default (callback?: () => void, options = {}) => {
  if (typeof callback !== 'function') {
    return;
  }
  if (loaded) {
    callback();
    return;
  }

  eventEmitter.once(EventsTypes.RECAPTCHA_LOADED, () => {
    callback();
  });

  loadScript(CAPTCHA_URL, options, () => {
    loaded = true;
  });
};
